import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FaTimes } from 'react-icons/fa';
import { closeDetail } from '../../reducers/notificationsSlice';

const formatFecha = (iso) => {
  try {
    return new Date(iso).toLocaleString();
  } catch (e) {
    return '';
  }
};

/**
 * Modal de detalle de una notificación (anuncios de administrador).
 *
 * Las agendas navegan a su enlace; los anuncios no tienen pantalla propia, así
 * que abren este modal con todo su contenido. El estado vive en
 * `notifications.detalle` (openDetail/closeDetail), así que el mismo modal
 * sirve al clic del dropdown y al de un push en vivo.
 *
 * Reservado para el futuro: si el ítem trae `images`, se pinta una miniatura
 * por cada una.
 */
export default function NotificationDetailModal() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const detalle = useSelector((state) => state.notifications.detalle);

  // Escape cierra, mientras haya algo abierto.
  useEffect(() => {
    if (!detalle) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') dispatch(closeDetail());
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [detalle, dispatch]);

  if (!detalle) return null;

  const cerrar = () => dispatch(closeDetail());

  const irAlEnlace = () => {
    const link = detalle.link;
    cerrar();
    if (link) navigate(link);
  };

  return createPortal(
    <div
      className="detail-overlay"
      data-testid="detail-overlay"
      onClick={cerrar}
    >
      <div
        className="detail-card"
        role="dialog"
        aria-modal="true"
        aria-label={detalle.title}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="detail-header">
          <strong className="detail-title">{detalle.title}</strong>
          <button
            type="button"
            className="detail-close"
            aria-label="Cerrar"
            onClick={cerrar}
          >
            <FaTimes />
          </button>
        </div>

        <p className="detail-message">{detalle.message}</p>
        <span className="detail-date">{formatFecha(detalle.created)}</span>

        {Array.isArray(detalle.images) && detalle.images.length > 0 && (
          <div className="detail-images">
            {detalle.images.map((src, index) => (
              <img key={src + index} src={src} alt="" className="detail-image" />
            ))}
          </div>
        )}

        {detalle.link && (
          <div className="detail-footer">
            <button type="button" className="detail-link-btn" onClick={irAlEnlace}>
              Ir al enlace
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
